import React, { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { EventPollQuestion } from '../../../interfaces/interfaces';
import { setEventPoll, setMeetingPoll } from '../../../reduxToolkit/slices/commonSlice';
import { setAnsweredPollQuestions } from '../../../reduxToolkit/slices/videoSlice';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { getPollsPerEvent, getPollsPerMeeting, getQuestionForUser } from '../../../services/services';
import NoPollActive from './NoPollActive';
import { SinglePoll } from './SinglePoll';
import { usePermission } from '../../../hooks/usePermission';

interface PollStageProps {
  isAdminParticipating?: boolean;
  allowUnregistered?: boolean;
  isMBW?: boolean;
  showOnlyOneQuestion?: boolean;
  backgroundColor?: string;
  textColor?: string;
}

const PollStage = ({ isAdminParticipating, allowUnregistered, isMBW, showOnlyOneQuestion, backgroundColor, textColor }: PollStageProps) => {
  const event = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);
  const meeting = useSelector(({ video }) => video.meeting);

  const polls = useSelector((state) => (meeting ? state.common.meetingPoll : state.common.eventPoll), shallowEqual);
  const eventChannel = useSelector((state) => state.common.pusherChannels.eventChannel);

  const eventPollsRef = useRef<EventPollQuestion[]>();
  const dispatch = useDispatch();
  const { isBoothHost } = usePermission();

  const fetchData = async () => {
    try {
      if (event && !meeting) {
        const _polls = await getPollsPerEvent(event._id);
        dispatch(setEventPoll(_polls));
      }
      if (meeting) {
        const _meetingPolls = await getPollsPerMeeting(meeting._id);
        dispatch(setMeetingPoll(_meetingPolls));
        const _answeredMeetingQuestions = _meetingPolls.filter((poll: EventPollQuestion) => poll.isAnswered).map((poll: EventPollQuestion) => poll._id);
        dispatch(setAnsweredPollQuestions(_answeredMeetingQuestions));
      }
    } catch (error) {
      console.error(error);
    }
  };

  function getQuestionToDisplay() {
    if (showOnlyOneQuestion) {
      const currentQuestion = polls?.find((q) => !q.isAnswered);
      if (currentQuestion) return [currentQuestion];
    }
    return polls;
  }

  const showPollStage =
    user?.role !== 'ORGANIZATION_EXTERNAL_SPEAKER' &&
    event &&
    ((user?.organization?._id !== event?.organization?._id && !isBoothHost(user, meeting?.booth ? [meeting.booth] : undefined)) || isAdminParticipating);

  useEffect(() => {
    eventPollsRef.current = polls;
  }, [polls]);

  useEffect(() => {
    if (!eventChannel) return;
    eventChannel.bind(`pollUpdated`, async ({ data }: { data: EventPollQuestion }) => {
      // even though we have the question in the redux state, we need to fetch it again to get the "personalized" data for the current user
      const [questionForUser] = await getQuestionForUser(data._id, user?._id);

      const index = eventPollsRef.current?.findIndex((question: EventPollQuestion) => {
        return question._id === data?._id;
      });

      if (index === -1) {
        // if we don't have the question in the redux we just append it to the state
        dispatch(setEventPoll([...(eventPollsRef.current ? eventPollsRef.current : []), questionForUser]));
      } else {
        // otherwise we update the question in the state
        if (eventPollsRef.current) {
          dispatch(
            setEventPoll([
              ...eventPollsRef.current.map((question: EventPollQuestion) => {
                if (question._id === data._id) return { ...questionForUser };
                else return question;
              }),
            ])
          );
        }
      }
    });
    eventChannel.bind('update-poll-on-delete-registration', fetchData);

    return () => {
      eventChannel.unbind(`pollUpdated`);
      eventChannel.unbind('update-poll-on-delete-registration');
    };
  }, [eventChannel, user]);

  useEffect(() => {
    fetchData();
  }, [event, meeting, user]);

  if (!showPollStage) return null;

  return (
    <div className="flex flex-col p-2 w-full h-full">
      {getQuestionToDisplay()
        ?.filter((el) => el.isActive)
        .map((question: EventPollQuestion) => {
          return (
            <SinglePoll
              key={question._id}
              question={question}
              allowUnregistered={allowUnregistered}
              event={event}
              isMBW={isMBW}
              backgroundColor={backgroundColor}
              textColor={textColor}
              fetch={fetchData}
            />
          );
        })}
      {(polls?.length === 0 || polls?.filter((el) => el.isActive).length === 0) && (
        <NoPollActive isMBW={isMBW} backgroundColor={backgroundColor} textColor={textColor} />
      )}
    </div>
  );
};

export default PollStage;
